import * as React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { filter, findIndex, isEmpty, remove } from 'lodash';
import { isNullOrUndefined } from 'util';

import {
  IControlError,
  IControlSchema,
  IControlSchemaBase,
} from '../../core/Controls';

import {
  b64toBlob,
  blobToFile,
  checkFileExtension,
  Component,
  getFiletExtension,
  Guid,
  I18NString,
} from '../helpers/Utils';
import { IDispatcher } from '../helpers/Dispatcher';
import { ERRORS_MESSAGES } from '../constants';

import { File } from './components/File';
import Field from './components/Field';
import ErrorAlert from './components/ErrorAlert';
import { createEmptyInput, createValueToSend } from './Files/helpers';

const style = { margin: '0 0 10px 0' };

const DEFAULT_FILE_SIZE = 1048576;
const DEFAULT_MAX_SIZE = DEFAULT_FILE_SIZE * 10;

export interface IFileSchema extends IControlSchemaBase {
  maxSize: number;
  format: Array<string>;
  description: I18NString;
  notification: I18NString;
  value?: any;
  isEnableComment: boolean;
  disabled?: boolean;
  visible?: boolean;
  maxFiles?: number;
  extra?: any;
  activeDeleteBtn?: any;
}

interface Props {
  schema: IFileSchema;
  api: any;
  dispatcher: IDispatcher;
  error: IControlError | undefined;
  controlValue?: any;
  someProperties?: Record<string, any>;
}

interface FileValueItem {
  id: string;
  value: string;
  url: string;
  isLoader: boolean;
}

interface State {
  inputs: Array<FileValueItem>;
  isComment: boolean;
  comment: string;
  showHelp: boolean;
  showNotification: boolean;
  warningDesc: string;
}

interface CheckBoxProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const TextArea = ({ onChange, onBlur, value, disabled }) => (
  <textarea
    disabled={disabled}
    onChange={onChange}
    onBlur={onBlur}
    value={value}
    className="form-control"
    placeholder="Комментарий"
  />
);

const CheckBox = ({ label, onChange, checked, disabled }: CheckBoxProps) => (
  <div className="checkbox">
    <label htmlFor="ch">
      <input
        disabled={disabled}
        onChange={onChange}
        type="checkbox"
        name="ch"
        checked={checked}
      />
      {label}
    </label>
  </div>
);

const isExist = (inputs: Array<FileValueItem>) => {
  if (inputs.length > 0) {
    for (const input of inputs) {
      if (input.url !== null || input.isLoader) {
        return true;
      }
    }
  }
  return false;
};

const isLoader = (inputs: Array<FileValueItem>) => {
  if (inputs !== null && inputs.length > 0) {
    for (const input of inputs) {
      if (input.isLoader) {
        return true;
      }
    }
  }
  return false;
};

const createEmtyInput = () => ({
  id: Guid.newGuid(),
  value: null,
  url: null,
  isLoader: false,
});

export default class MultipleFiles extends Component<Props, State> {
  state = {
    inputs: [createEmtyInput()],
    isComment: false,
    comment: '',
    showHelp: false,
    showNotification: true,
    warningDesc: '',
  };

  componentDidMount() {
    const {
      schema: { value },
    } = this.props;
    const { controlValue } = this.props;

    if (controlValue) {
      this.setState({
        ...controlValue,
        showHelp: false,
        showNotification: true,
      });
      return;
    }

    if (!isNullOrUndefined(value)) {
      if (value.inputs && value.inputs.length === 0)
        this.setState({
          ...value,
          inputs: [createEmtyInput()],
          showHelp: false,
          showNotification: true,
        });
      else this.setState({ ...value, showHelp: false, showNotification: true });
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const prevValue = prevProps.schema.value;
    const { value } = this.props.schema;

    if (this.props?.someProperties?.shouldReset) {
      const { inputs } = this.state;
      inputs.forEach((el) => this.onRemove(el, null));
    }

    if (prevValue?.inputs?.length !== value?.inputs?.length) {
      if (value?.inputs?.[0]?.isBase64) {
        this.onUploadFile(value.inputs[0]);
      }

      if (!value?.inputs?.length && !!prevState?.inputs?.length) {
        this.setState({ inputs: [createEmtyInput()] });
      }
    }
  }

  onAdd = () => {
    const id = Guid.newGuid();

    this.setState(({ inputs }) => ({
      inputs: [{ id, value: null, url: null, isLoader: false }, ...inputs],
    }));
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { comment } = this.state;
    const { schema, dispatcher } = this.props;

    if (!event.target.checked) {
      dispatcher.onBlur(schema.id, null, true, undefined);
      comment = '';
    }

    this.setState({
      isComment: event.target.checked,
      comment,
    });
  };

  onBlurComment = () => {
    const { schema, dispatcher } = this.props;
    const { comment, inputs } = this.state;

    if (!isEmpty(comment)) {
      dispatcher.onBlur(
        schema.id,
        this.toJSON(this.state, inputs),
        true,
        undefined,
      );
    } else {
      dispatcher.onBlur(schema.id, null, true, undefined);
    }
  };

  onChangeComment = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      comment: event.target.value,
    });
  };

  onRemove = (el: FileValueItem, _e: React.ChangeEvent<HTMLInputElement>) => {
    const loaders = [...this.state.inputs];

    const idx = findIndex(loaders, (o) => o.id === el.id);
    const inputs = remove(
      this.state.inputs,
      (o: FileValueItem) => o.id !== el.id,
    );

    // TODO: need replace on this
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const customCb = this.props?.someProperties?.customDelete;

    if (el.url !== null) {
      loaders[idx].isLoader = true;
      self.setState({ inputs: loaders });
      this.props.api.deleteFileValue(
        this.props.schema.id,
        el.value,
        self.toJSON(self.state, inputs),
        el.id,
        {
          success: () => {
            self.props.dispatcher.onLoadFile(
              this.props.schema.id,
              inputs.length > 0 ? { ...this.state, inputs, error: null } : null,
            );
            if (inputs.length === 0) {
              inputs.push(createEmtyInput());
            }

            this.props.dispatcher.onBlur(
              this.props.schema.id,
              createValueToSend({ inputs }),
              true,
              undefined,
              {},
              // @ts-ignore
              'update',
            );

            if (!!this.props.schema.extra.onChangeSuccess)
              this.props.schema.extra.onChangeSuccess();

            self.setState({
              inputs,
            });
          },
        },
      );
    } else {
      if (inputs.length === 0) {
        self.props.dispatcher.onLoadFile(self.props.schema.id, null);
        inputs.push(createEmtyInput());
      }

      self.setState({
        inputs,
      });
    }

    if (customCb && typeof customCb === 'function') {
      customCb();
    }
  };

  onUploadFile = async (data) => {
    const blob = await b64toBlob(data.url);
    const element = blobToFile(blob, data.value);
    const options = {
      id: this.props.schema.id,
      maxSize: 0,
      format: 'PDF',
      element,
    };

    data.url = this.props.api?.getFileUrl(data.id, 'document');
    data.isLoader = true;
    const inputs = [data];

    this.setState({ inputs });

    if (!this.props.api?.setBlobFileValue) {
      console.error('need ApiClient setBlobFileValue');
    }

    // eslint-disable-next-line no-unused-expressions
    this.props.api?.setBlobFileValue &&
      this.props.api.setBlobFileValue(
        options,
        this.toJSON(this.state, inputs),
        data.id,
        {
          success: (response) => {
            inputs[0].value = response?.value ?? data.value;
            // inputs[idx].url = response.url;
            inputs[0].isBase64 = false;
            inputs[0].isLoader = false;

            this.setState({
              inputs,
              warningDesc: '',
            });

            // this.props.dispatcher.onLoadFile(this.props.schema.id, { ...this.state, error: null });
            this.props.dispatcher.onBlur(
              this.props.schema.id,
              createValueToSend({ inputs }),
              true,
              undefined,
            );
            this.props.dispatcher.setError(
              this.props.schema.id,
              undefined,
              undefined,
              true,
            );
            if (!!this.props.schema.extra.onChangeSuccess)
              this.props.schema.extra.onChangeSuccess();
          },
          error: (status: number, _msg: string) => {
            inputs[0] = {
              ...inputs[0],
              value: null,
              url: null,
              isLoader: false,
            };

            this.setState({
              inputs,
              warningDesc: '',
            });

            if (status === 401) {
              window.location.href = '/#/not-authorized';
            } else {
              confirmAlert({
                message: ERRORS_MESSAGES?.[status] ?? 'Что-то пошло не так',
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                buttons: [{ label: 'ОК', onClick: () => {} }],
              });
            }
          },
        },
      );
  };

  onUpload = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const inputs = [...this.state.inputs];
    const { files: _files } = event.target;
    const idx = findIndex(inputs, (o) => o.id === id);
    const maxSizeLimit = this.props.schema.extra?.maxSize ?? DEFAULT_MAX_SIZE;

    if (_files.length === 0) return;

    const filename = _files[0].name;
    const options = {
      id: this.props.schema.id,
      maxSize: 0,
      format: 'PDF',
      element: event.target,
    };

    if (
      idx > -1 &&
      checkFileExtension(filename, this.props.schema.format) &&
      _files[0].size <= maxSizeLimit
    ) {
      inputs[idx].value = filename;
      inputs[idx].url = this.props.api?.getFileUrl(id, 'document');
      inputs[idx].isLoader = true;

      this.setState({ inputs });

      this.props.api.setFileValue(
        options,
        this.toJSON(this.state, inputs),
        id,
        {
          success: (response) => {
            inputs[idx].value = response?.value ?? filename;
            // inputs[idx].url = response.url;
            inputs[idx].isLoader = false;

            this.setState({
              inputs,
              warningDesc: '',
            });

            this.props.dispatcher.onLoadFile(this.props.schema.id, {
              ...this.state,
              error: null,
            });
            this.props.dispatcher.onBlur(
              this.props.schema.id,
              createValueToSend({ inputs }),
              true,
              undefined,
              {},
              // @ts-ignore
              'update',
            );
            // this.props.dispatcher.onBlur(
            //   this.props.schema.id,
            //   { inputs: withoutRemoved },
            //   true,
            //   undefined,
            //   {},
            //   // @ts-ignore
            //   'update',
            // );
            this.props.dispatcher.setError(
              this.props.schema.id,
              undefined,
              undefined,
              true,
            );
          },
          error: (status: number, _msg: string) => {
            inputs[idx] = {
              ...inputs[idx],
              value: null,
              url: null,
              isLoader: false,
            };

            this.setState({
              inputs,
              warningDesc: '',
            });

            if (status === 401) {
              window.location.href = '/#/not-authorized';
            } else {
              confirmAlert({
                message: ERRORS_MESSAGES?.[status] ?? 'Что-то пошло не так',
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                buttons: [{ label: 'ОК', onClick: () => {} }],
              });
            }
          },
        },
      );
    } else {
      let msg = '';

      if (!checkFileExtension(filename, this.props.schema.format)) {
        msg = `Неверный формат файла: ${getFiletExtension(filename)}`;
      } else if (_files[0].size > maxSizeLimit) {
        msg = `Размер файла превышает допустимый размер в ${Math.trunc(
          maxSizeLimit / DEFAULT_FILE_SIZE,
        )} Мб`;
      }
      this.setState({
        warningDesc: msg,
      });
      // this.props.dispatcher.setError(this.props.schema.id, undefined, { message: msg }, true);
    }
  };

  createInputFile = (
    isMain: boolean,
    el: FileValueItem,
    showButtons,
    showAdd: boolean,
  ) => {
    const options = {
      ...this.props,
      ...el,
      disabled: this.props.schema.disabled,
    };
    const { disabled, value, activeDeleteBtn } = options.schema;

    let isDeleteDisabled = false;
    if (typeof activeDeleteBtn === 'boolean')
      isDeleteDisabled = activeDeleteBtn;
    if (disabled === true) isDeleteDisabled = true;

    return (
      <div className="input-group" style={style} key={`File.${el.id}`}>
        <File
          {...options}
          onChange={this.onUpload}
          isBase64={value?.inputs?.[0]?.isBase64}
        />
        <span className="input-group-btn">
          {!this.props.schema.disabled && this.props.schema?.notification && (
            <button
              type="button"
              onClick={this.onClickNotification}
              className="btn btn-default btn-add"
            >
              <span className="glyphicon glyphicon-info-sign" />
            </button>
          )}
          {!this.props.schema.disabled && (
            <button
              type="button"
              onClick={this.onClickHelp}
              className="btn btn-default btn-add"
            >
              <span className="glyphicon glyphicon-question-sign" />
            </button>
          )}
          {showButtons && isMain && showAdd && (
            <button
              disabled={disabled}
              title="Добавить еще"
              type="button"
              className="btn btn-default btn-add"
              onClick={this.onAdd}
            >
              <span className="glyphicon glyphicon-plus-sign" />
            </button>
          )}
          {((showButtons && isMain && el.value !== null) ||
            (showButtons && !isMain)) && (
            <button
              disabled={isDeleteDisabled}
              type="button"
              className="btn btn-danger btn-remove"
              onClick={(event: any) => this.onRemove(el, event)}
            >
              Удалить
            </button>
          )}
        </span>
      </div>
    );
  };

  onClickHelp = () => {
    this.setState(({ showHelp }) => ({
      showHelp: !showHelp,
    }));
  };

  onClickNotification = () => {
    this.setState(({ showNotification }) => ({
      showNotification: !showNotification,
    }));
  };

  // eslint-disable-next-line class-methods-use-this
  toJSON(state: State, inputs: Array<any>) {
    const inputsData = filter(inputs, (obj) => obj.url !== null).map((obj) => ({
      id: obj.id,
      url: obj.url,
      value: obj.value,
    }));

    if (state.isComment) {
      return { isComment: state.isComment, comment: state.comment, inputs: [] };
    }
    return inputsData.length > 0
      ? JSON.stringify({
          isComment: state.isComment,
          comment: state.comment,
          inputs: inputsData,
        })
      : '';
  }

  render() {
    const {
      schema: { isEnableComment, disabled, extra },
      error,
    } = this.props;

    const maxSizeLimit = this.props.schema.extra?.maxSize ?? DEFAULT_MAX_SIZE;
    const errorMessage = error && error.message;
    const isShowButtons = !isLoader(this.state.inputs);
    const fieldState =
      isExist(this.state.inputs) ||
      (this.state.isComment && this.state.comment !== '')
        ? 'success'
        : 'default';

    return !this.state.isComment || !isEnableComment ? (
      <Field {...this.props.schema} status={fieldState}>
        {extra && extra.hint && (
          <div
            className="alert alert-info hint"
            dangerouslySetInnerHTML={{ __html: extra.hint }}
          />
        )}
        {this.state.inputs.map((item: FileValueItem, idx: number) =>
          this.createInputFile(
            idx === this.state.inputs.length - 1,
            item,
            isShowButtons,
            this.state.inputs.length < this.props.schema.maxFiles,
          ),
        )}
        {isEnableComment && !isExist(this.state.inputs) && (
          <CheckBox
            disabled={disabled}
            label="Нет файлов?"
            onChange={this.onChange}
            checked={this.state.isComment}
          />
        )}
        <ErrorAlert errorMessage={errorMessage} />
        {this.state.showNotification && this.props.schema?.notification && (
          <div className="alert alert-warning" role="alert">
            {this.props.schema.notification && (
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: this.props.schema?.notification?.ru,
                  }}
                />
                <br />
              </div>
            )}
          </div>
        )}
        {this.state.showHelp && (
          <div className="alert alert-warning" role="alert">
            {this.props.schema.description && (
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: this.props.schema.description.ru,
                  }}
                />
                <br />
              </div>
            )}
            {this.props.schema.format.length ? (
              <>
                Допустимые форматы файлов для загрузки:{' '}
                {this.props.schema.format.join(',')}.<br />
                Максимальный размер файла не должен превышать{' '}
                {Math.trunc(maxSizeLimit / DEFAULT_FILE_SIZE)}Мб
              </>
            ) : null}
          </div>
        )}
        {this.state.warningDesc && (
          <div className="alert alert-warning" role="alert">
            {this.state.warningDesc}
          </div>
        )}
      </Field>
    ) : (
      <Field {...this.props.schema} status={fieldState}>
        {extra && extra.hint && (
          <div
            className="alert alert-info hint"
            dangerouslySetInnerHTML={{ __html: extra.hint }}
          />
        )}
        <TextArea
          disabled={disabled}
          onBlur={this.onBlurComment}
          onChange={this.onChangeComment}
          value={this.state.comment}
        />
        <CheckBox
          disabled={disabled}
          label="Нет файлов?"
          onChange={this.onChange}
          checked={this.state.isComment}
        />
        <ErrorAlert errorMessage={errorMessage} />
        {this.state.warningDesc && (
          <div className="alert alert-warning" role="alert">
            {this.state.warningDesc}
          </div>
        )}
      </Field>
    );
  }
}

export function createMultiFiles(
  schema: IControlSchema,
  api: any,
  dispatcher: IDispatcher = null,
  error: IControlError | undefined,
) {
  return (
    <MultipleFiles
      schema={schema as IFileSchema}
      api={api}
      dispatcher={dispatcher}
      error={error}
    />
  );
}
